/**
 * <DeviceCheckGuidance> — ce qu'il faut faire, un geste par ligne (molecule).
 *
 * L'ancien `TypedGuidance` : sous une étape du test du matériel, le conseil du moment — « Monte le
 * son de ton ordinateur », « Choisis « Autoriser » pour le micro ». Un geste par ligne, chacun
 * avec son glyphe quand il en a un ; une phrase seule n'a ni glyphe ni puce. Affiché d'un bloc :
 * plus d'écriture lettre à lettre, un élève de dix ans lit lentement.
 *
 * `permission` dessine, sous une ligne, ce que l'élève va voir s'ouvrir dans son navigateur — deux
 * lignes, Micro et Caméra, et « Autoriser » à choisir — sans dire OÙ cliquer : les navigateurs
 * déplacent leurs boutons d'une version à l'autre.
 *
 * Un bloc encastré dans l'étape (`<Card elevation="none">`) : il est posé dans la carte du test.
 *
 * Composes DS pieces only — `<Card>`, `<IconBubble>`, `<Text>`, `<Badge>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { Card } from "../Card"

/** Un geste du conseil. À remonter dans `ports/primary/ui/types` au câblage. */
export type UIGuidanceLine = { text: string; iconName?: IconName }

export type DeviceCheckGuidanceProps = {
    lines: UIGuidanceLine[]
    /**
     * La fenêtre d'autorisation du navigateur, dessinée sous la ligne `afterIndex` ; l'appareil de
     * l'étape y est mis en avant.
     */
    permission?: { device: "microphone" | "camera"; afterIndex: number }
    testID?: string
}

const TEXTS = {
    microphone: "Micro",
    camera: "Caméra",
    allow: "Autoriser",
    block: "Bloquer",
}

/** La pastille d'un geste : plus petite que celle d'une étape, à laquelle elle ne doit pas ressembler. */
const LINE_BUBBLE = 24

export function DeviceCheckGuidance(props: DeviceCheckGuidanceProps) {
    const { lines, permission, testID } = props
    const bulleted = lines.length > 1

    return (
        <Card elevation="none" background="surface-raised" p="3" testID={testID}>
            <View style={styles.lines}>
                {lines.map((line, index) => (
                    <View key={line.text} style={styles.lineBlock}>
                        <View style={styles.line}>
                            {line.iconName != null ? (
                                <IconBubble
                                    iconName={line.iconName}
                                    size={LINE_BUBBLE}
                                    iconSize={14}
                                    color="primary-soft"
                                    iconColor="primary"
                                />
                            ) : (
                                bulleted && <View style={styles.dot} />
                            )}
                            <Text size="text-base" color="text-primary" style={styles.text}>
                                {frenchSpacing(line.text)}
                            </Text>
                        </View>
                        {permission?.afterIndex === index && <PermissionWindow device={permission.device} />}
                    </View>
                ))}
            </View>
        </Card>
    )
}

/** Les deux lignes de la fenêtre d'autorisation ; celle de l'étape en avant. */
function PermissionWindow(props: { device: "microphone" | "camera" }) {
    return (
        <View style={styles.window}>
            {(["microphone", "camera"] as const).map((device) => {
                const active = device === props.device
                return (
                    <View key={device} style={styles.windowRow}>
                        <Text
                            size="text-sm"
                            weight={active ? "bold" : "regular"}
                            color={active ? "text-primary" : "text-muted"}
                            style={styles.windowName}
                        >
                            {TEXTS[device]}
                        </Text>
                        <Badge text={TEXTS.allow} variant={active ? "primary" : "neutral"} />
                        <Text size="text-sm" color="text-muted">
                            {TEXTS.block}
                        </Text>
                    </View>
                )
            })}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    lines: {
        gap: theme.spacing["3"],
    },
    lineBlock: {
        gap: theme.spacing["2"],
    },
    line: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: theme.spacing["3"],
    },
    /** La puce d'une phrase sans glyphe, au milieu de la première ligne de texte. */
    dot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        marginTop: theme.spacing["2"],
        backgroundColor: theme.colors["text-muted"],
    },
    text: {
        flex: 1,
    },
    /** Le schéma prolonge la phrase : il s'aligne sur son texte, pas sur sa pastille. */
    window: {
        alignSelf: "flex-start",
        marginLeft: LINE_BUBBLE + theme.spacing["3"],
        gap: theme.spacing["2"],
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors["surface-raised"],
    },
    windowRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    windowName: {
        width: 56,
    },
}))
